/**
 * Internal lender fee rules engine — client-safe types and pure evaluation.
 *
 * The pricing engine (Optimal Blue) remains the ONLY source of rate, APR,
 * price/par/points/credit, lock, eligibility, P&I and our compensation
 * economics. It is NOT the source of borrower-facing lender fees. Those come
 * from the lender fee schedules we maintain internally, in
 * `lender-fee-schedules.server.ts`, keyed by the investor the pricing engine
 * returned for that exact quote.
 *
 * Rules of this module:
 *  - Nothing is invented. A fee exists only when a supplied schedule states it.
 *  - A wholesale lender/investor name never appears in a published label.
 *  - The "Estimated lender fees" total contains only known, fixed dollar
 *    amounts whose condition is satisfied by the priced scenario.
 *  - Ranges, unknown amounts and third-party costs are listed separately and
 *    never rolled into that total.
 *  - Nothing here is ever presented as cash to close.
 */

/** Program families a schedule can apply to. */
export type FeeProgramFamily = "DSCR" | "NonQM" | "ForeignNational";

/** Scenario facts a rule may condition on. */
export type FeeContext = {
  programFamily: FeeProgramFamily;
  state: string;
  loanAmount: number;
  entityVesting: boolean;
  trustVesting: boolean;
  shortTermRental: boolean;
  borrowerCount: number;
  propertyType: string;
  loanPurpose: "purchase" | "rate_term_refinance" | "cash_out_refinance";
};

/** How certain the amount is, which decides where the fee is displayed. */
export type FeeCertainty =
  /** Known fixed dollar amount, condition satisfied → counted in the total. */
  | "fixed"
  /** Known amount but the trigger depends on facts we do not have yet. */
  | "conditional"
  /** Published as a range or "varies" → shown as an estimate only. */
  | "variable"
  /** Schedule says a fee applies but the amount was not supplied. */
  | "unknown";

export type FeeCategory = "lender" | "third_party";

/**
 * Audit classification for how a fee relates to the DSCR / Non-QM scenario.
 *  A) always            — the lender charges it on every DSCR file of this program.
 *  B) conditional       — charged only when a scenario fact is known true.
 *  C) variable          — published as a range / "varies" / third-party invoice.
 *  D) not_applicable    — on the general sheet but not a DSCR charge.
 *  E) review            — the supplied schedule does not clearly establish
 *                         DSCR applicability. Never auto-added; flagged instead.
 */
export type DscrApplicability =
  | "always"
  | "conditional"
  | "variable"
  | "not_applicable"
  | "review";

export type LenderFeeRule = {
  /** Stable id used for audit snapshots. */
  id: string;
  /**
   * Consumer-safe label. This is the lender's OFFICIAL fee name from the
   * supplied schedule (abbreviations may be expanded for clarity), never an
   * invented generic name. It never contains a lender/investor name.
   */
  label: string;
  /** Audit classification. Drives what may enter the fixed headline total. */
  dscrApplicability: DscrApplicability;
  programFamilies: FeeProgramFamily[];
  certainty: FeeCertainty;
  category: FeeCategory;
  /** Fixed dollar amount, when the schedule supplies one. */
  amount?: number;
  /** Published range, when the schedule supplies one instead of an amount. */
  min?: number;
  max?: number;
  /** Multiplied by the borrower count when true. */
  perBorrower?: boolean;
  /** Satisfied → the fee applies. Absent → always applies. */
  applies?: (ctx: FeeContext) => boolean;
  /** Plain-English trigger shown to the borrower for conditional items. */
  conditionLabel?: string;
  /** Extra consumer-safe explanation. */
  note?: string;
  /** Effective / last-verified date of the supplied schedule. */
  effectiveDate?: string;
  /** INTERNAL ONLY — never rendered publicly. */
  sourceNote: string;
};

export type LenderFeeSchedule = {
  /** Internal investor key. Never published. */
  lenderKey: string;
  /** Internal display name for staff/admin only. */
  internalName: string;
  effectiveDate?: string;
  rules: LenderFeeRule[];
  /** Scenario-level caveats, e.g. "NC/NJ commitment fee amount not supplied". */
  verificationFlags?: Array<{ applies: (ctx: FeeContext) => boolean; message: string }>;
  /**
   * Fees present on the lender's general sheet that are documented as NOT
   * charged on a DSCR/Non-QM investor file. Kept for the audit trail so a
   * later reviewer can see the exclusion was deliberate, not an oversight.
   */
  notApplicableToDscr?: Array<{ officialName: string; reason: string }>;
  /**
   * Items the supplied schedule leaves ambiguous for DSCR. Never charged,
   * never totalled — surfaced to the loan officer for confirmation.
   */
  reviewItems?: Array<{ officialName: string; question: string }>;
};


/** One evaluated, borrower-facing fee line. */
export type AppliedFee = {
  id: string;
  /** Official fee name from the lender's schedule. */
  label: string;
  certainty: FeeCertainty;
  category: FeeCategory;
  /** Why this line is here — carried into the audit snapshot. */
  dscrApplicability: DscrApplicability;
  amount: number | null;
  min: number | null;
  max: number | null;
  conditionLabel: string | null;
  note: string | null;
  /** Effective / last-verified date of the source schedule, when supplied. */
  effectiveDate: string | null;
};

export type LenderFeeEstimate = {
  /** True when we hold a maintained schedule for this quote's investor. */
  scheduleFound: boolean;
  /** Internal key kept for audit; the UI never renders it. */
  lenderKey: string | null;
  programFamily: FeeProgramFamily;
  effectiveDate: string | null;
  /** Known, applicable, fixed lender fees. These sum to `fixedTotal`. */
  fixedFees: AppliedFee[];
  /** Known amounts whose trigger is scenario-dependent, plus ranges. */
  conditionalFees: AppliedFee[];
  /** Third-party / variable estimates, never part of the total. */
  variableCosts: AppliedFee[];
  fixedTotal: number | null;
  /** Items we deliberately refuse to guess. */
  verificationNotes: string[];
  /**
   * Audit trail: fees on the lender's general sheet deliberately excluded from
   * this DSCR quote, with the reason. Never rendered as a charge.
   */
  excludedFees: Array<{ officialName: string; reason: string }>;
  /** Consumer-safe disclosures rendered under the breakdown. */
  disclosures: string[];
};

export const THIRD_PARTY_COSTS_NOTE =
  "Third-party and prepaid costs — title/settlement, escrow, appraisal, recording charges, transfer taxes, attorney, homeowners insurance, property taxes and prepaid interest — are not included above and are not estimated here.";

/**
 * Primary consumer disclosure shown beside the Estimated Lender Fees figure.
 * Original wording — deliberately not copied from any third-party publisher.
 */
export const ESTIMATED_LENDER_FEES_DISCLOSURE =
  "Estimated lender fees are charges associated with originating and processing this loan and may include items such as underwriting, processing, administration, document preparation, or other lender-required charges. The amount shown does not represent all closing costs or cash required to close. Third-party costs such as appraisal, title/settlement, recording charges, taxes, insurance and other transaction-specific costs may apply. Your official Loan Estimate or other applicable disclosures will show the costs for your transaction.";

export const NOT_CASH_TO_CLOSE_NOTE =
  "Estimated lender fees are not your cash to close. Lender fees and third-party closing costs can change after application and property review. Your Loan Estimate or other applicable disclosures will show the costs for your transaction.";

export const APR_SOURCE_NOTE =
  "APR reflects applicable borrower-paid finance charges and pricing credits for this quote. Depending on the option, the APR shown is either the APR returned with that quote or our own audited calculation, used where verified borrower-paid finance charges outside the quoted price apply. Other lender and third-party costs may apply and may not be included in the APR. On these investor loans APR reflects the note rate plus any borrower-paid discount points, and pricing credits are applied only to offset borrower-paid finance charges, so an option at or near par can show an APR equal to its rate. APR is never shown below the note rate.";


export const NO_SCHEDULE_NOTE =
  "We do not yet hold a verified fee schedule for this option's lender, so no lender fee estimate is shown. Your loan officer confirms lender fees in writing before you proceed.";

/**
 * Official CFPB explainer. DSCR loans are frequently business-purpose and
 * non-owner-occupied, so we never promise a TRID Loan Estimate — the link is
 * offered as education only.
 */
export const CFPB_LOAN_ESTIMATE_URL = "https://www.consumerfinance.gov/owning-a-home/loan-estimate/";
export const CFPB_LOAN_ESTIMATE_LABEL = "Learn about Loan Estimate closing costs";


function money(n: number): string {
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/** Human summary of one fee line. Used in snapshots and emails. */
export function describeFee(f: AppliedFee): string {
  const amt =
    f.amount != null
      ? money(f.amount)
      : f.min != null && f.max != null
        ? `${money(f.min)}–${money(f.max)}`
        : "amount to be confirmed";
  return `${f.label}: ${amt}${f.conditionLabel ? ` (${f.conditionLabel})` : ""}`;
}

function toApplied(rule: LenderFeeRule, ctx: FeeContext): AppliedFee {
  const multiplier = rule.perBorrower ? Math.max(1, Math.round(ctx.borrowerCount)) : 1;
  const amount = rule.amount != null ? Math.round(rule.amount * multiplier * 100) / 100 : null;
  return {
    id: rule.id,
    label:
      rule.perBorrower && multiplier > 1 ? `${rule.label} (${multiplier} borrowers)` : rule.label,
    certainty: rule.certainty,
    category: rule.category,
    dscrApplicability: rule.dscrApplicability,
    amount,
    min: rule.min ?? null,
    max: rule.max ?? null,
    conditionLabel: rule.conditionLabel ?? null,
    note: rule.note ?? null,
    effectiveDate: rule.effectiveDate ?? null,
  };
}

/**
 * Pure evaluation of one lender's schedule against a priced scenario.
 * No I/O, no dates, no randomness — the same inputs always produce the same
 * estimate, which is what makes the stored audit snapshot meaningful.
 *
 * Only rules classified `always` (or `conditional` whose condition is known
 * true from the scenario) with a known fixed amount may enter `fixedTotal`.
 * `variable`, `review` and `not_applicable` never do.
 */
export function evaluateFeeSchedule(
  schedule: LenderFeeSchedule | null,
  ctx: FeeContext,
): LenderFeeEstimate {
  const base: LenderFeeEstimate = {
    scheduleFound: false,
    lenderKey: null,
    programFamily: ctx.programFamily,
    effectiveDate: null,
    fixedFees: [],
    conditionalFees: [],
    variableCosts: [],
    fixedTotal: null,
    verificationNotes: [],
    excludedFees: [],
    disclosures: [NO_SCHEDULE_NOTE, THIRD_PARTY_COSTS_NOTE, NOT_CASH_TO_CLOSE_NOTE],
  };
  if (!schedule) return base;

  const fixedFees: AppliedFee[] = [];
  const conditionalFees: AppliedFee[] = [];
  const variableCosts: AppliedFee[] = [];
  const verificationNotes: string[] = [];
  const excludedFees: Array<{ officialName: string; reason: string }> = [
    ...(schedule.notApplicableToDscr ?? []),
  ];

  for (const rule of schedule.rules) {
    // D) Not applicable to this product — recorded, never charged.
    if (rule.dscrApplicability === "not_applicable") {
      excludedFees.push({ officialName: rule.label, reason: rule.conditionLabel ?? "Not charged on this product." });
      continue;
    }
    if (!rule.programFamilies.includes(ctx.programFamily)) {
      excludedFees.push({
        officialName: rule.label,
        reason: "Not charged on this program family.",
      });
      continue;
    }
    // E) Ambiguous source → flag for review, never inflate the quote.
    if (rule.dscrApplicability === "review") {
      verificationNotes.push(
        `${rule.label}: the published schedule does not clearly establish whether this applies to a DSCR file, so it is excluded from the estimate and confirmed by your loan officer.`,
      );
      continue;
    }
    if (rule.applies && !rule.applies(ctx)) {
      excludedFees.push({
        officialName: rule.label,
        reason: rule.conditionLabel
          ? `Condition not met for this scenario: ${rule.conditionLabel}.`
          : "Condition not met for this scenario.",
      });
      continue;
    }
    const applied = toApplied(rule, ctx);
    if (rule.certainty === "fixed" && applied.amount != null) fixedFees.push(applied);
    else if (rule.certainty === "conditional") conditionalFees.push(applied);
    else if (rule.certainty === "variable") variableCosts.push(applied);
    else {
      conditionalFees.push(applied);
      verificationNotes.push(`${rule.label}: amount confirmed by your loan officer before closing.`);
    }
  }

  for (const flag of schedule.verificationFlags ?? []) {
    if (flag.applies(ctx)) verificationNotes.push(flag.message);
  }
  for (const item of schedule.reviewItems ?? []) {
    verificationNotes.push(`${item.officialName}: ${item.question}`);
  }

  const fixedTotal = fixedFees.length
    ? Math.round(fixedFees.reduce((s, f) => s + (f.amount ?? 0), 0) * 100) / 100
    : null;

  return {
    scheduleFound: true,
    lenderKey: schedule.lenderKey,
    programFamily: ctx.programFamily,
    effectiveDate: schedule.effectiveDate ?? null,
    fixedFees,
    conditionalFees,
    variableCosts,
    fixedTotal,
    verificationNotes,
    excludedFees,
    disclosures: [ESTIMATED_LENDER_FEES_DISCLOSURE, THIRD_PARTY_COSTS_NOTE, NOT_CASH_TO_CLOSE_NOTE],
  };
}

export const EMPTY_FEE_ESTIMATE: LenderFeeEstimate = {
  scheduleFound: false,
  lenderKey: null,
  programFamily: "DSCR",
  effectiveDate: null,
  fixedFees: [],
  conditionalFees: [],
  variableCosts: [],
  fixedTotal: null,
  verificationNotes: [],
  excludedFees: [],
  disclosures: [NO_SCHEDULE_NOTE, THIRD_PARTY_COSTS_NOTE, NOT_CASH_TO_CLOSE_NOTE],
};

